import { createBrowserRouter, Link, useRouteError } from "react-router";
import { Layout, PublicLayout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Gift, RotateCcw, Home, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded pages
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const GiftWizard = lazy(() => import("@/pages/GiftWizard"));
const GiftResults = lazy(() => import("@/pages/GiftResults"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Cards = lazy(() => import("@/pages/Cards"));
const CardEditor = lazy(() => import("@/pages/CardEditor"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const GiftExchange = lazy(() => import("@/pages/GiftExchange"));
const GroupGift = lazy(() => import("@/pages/GroupGift"));
const WishList = lazy(() => import("@/pages/WishList"));
const Profile = lazy(() => import("@/pages/Profile"));
const ReminderSettings = lazy(() => import("@/pages/ReminderSettings"));
const GiftDna = lazy(() => import("@/pages/GiftDna"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("@/pages/admin/AdminUserDetail"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminEmailPreview = lazy(() => import("@/pages/admin/AdminEmailPreview"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AdminAuditLog"));
const NotificationMonitoring = lazy(() => import("@/pages/admin/NotificationMonitoring"));
const AdminDeployments = lazy(() => import("@/pages/admin/AdminDeployments"));
const AdminMigrations = lazy(() => import("@/pages/admin/AdminMigrations"));
const AdminQueueMonitoring = lazy(() => import("@/pages/admin/AdminQueueMonitoring"));
const AdminSharePreview = lazy(() => import("@/pages/admin/AdminSharePreview"));
const AdminOgPreview = lazy(() => import("@/pages/admin/AdminOgPreview"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminNewsletter = lazy(() => import("@/pages/admin/AdminNewsletter"));
const AdminGSC = lazy(() => import("@/pages/admin/AdminGSC"));
const AdminExperienceDeals = lazy(() => import("@/pages/admin/AdminExperienceDeals"));
const AdminFeeds = lazy(() => import("@/pages/admin/AdminFeeds"));
const Featured = lazy(() => import("@/pages/Featured"));
const BrowseGifts = lazy(() => import("@/pages/BrowseGifts"));
const Experiences = lazy(() => import("@/pages/Experiences"));
const Shop = lazy(() => import("@/pages/Shop"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const GoogleContactsCallback = lazy(() => import("@/pages/GoogleContactsCallback"));
const CardView = lazy(() => import("@/pages/CardView"));
const MyCards = lazy(() => import("@/pages/MyCards"));
const CardAnalytics = lazy(() => import("@/pages/CardAnalytics"));
const SavedGifts = lazy(() => import("@/pages/SavedGifts"));
const GiftHistory = lazy(() => import("@/pages/GiftHistory"));
const GiftImpact = lazy(() => import("@/pages/GiftImpact"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const UnlockAccount = lazy(() => import("@/pages/UnlockAccount"));
const PreferenceForm = lazy(() => import("@/pages/PreferenceForm"));
const ContactOnboarding = lazy(() => import("@/pages/ContactOnboarding"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const GiftsFor = lazy(() => import("@/pages/GiftsFor"));
const AffiliateDisclosure = lazy(() => import("@/pages/AffiliateDisclosure"));
const Unsubscribed = lazy(() => import("@/pages/Unsubscribed"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="space-y-4 w-full max-w-md p-8">
        <Skeleton className="h-8 w-3/4 mx-auto" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-32 w-full mt-4 rounded-xl" />
      </div>
    </div>
  );
}

function PageErrorFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="text-center space-y-5 max-w-sm">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-red-500/15 flex items-center justify-center">
          <AlertTriangle className="h-7 w-7 text-red-400" />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-xl font-bold">Page crashed</h2>
          <p className="text-sm text-muted-foreground">
            Something went wrong loading this page. Your other pages are still working.
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            <RotateCcw className="h-3.5 w-3.5 mr-2" />
            Reload
          </Button>
          <Button variant="gradient" size="sm" asChild>
            <Link to="/dashboard">
              <Home className="h-3.5 w-3.5 mr-2" />
              Dashboard
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary fallback={<PageErrorFallback />}>
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function WizardErrorBoundary() {
  const error = useRouteError();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="text-center space-y-6 max-w-sm">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-gift-purple-500/20 flex items-center justify-center">
          <Gift className="h-8 w-8 text-gift-purple-400" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Gift wizard crashed</h2>
          <p className="text-muted-foreground text-sm">
            Something went wrong while loading the wizard. Start fresh or go back to your dashboard.
          </p>
        </div>
        {error instanceof Error && (
          <pre className="text-xs text-red-400/70 bg-red-500/5 rounded-lg p-3 text-left overflow-auto max-h-24">
            {error.message}
          </pre>
        )}
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" asChild>
            <Link to="/dashboard">
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Link>
          </Button>
          <Button variant="gradient" asChild>
            <Link to="/wizard">
              <RotateCcw className="h-4 w-4 mr-2" />
              Start Over
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

// SEO page component for /gifts-for/:slug
function GiftsForPage() {
  return (
    <SuspenseWrapper>
      <GiftsFor />
    </SuspenseWrapper>
  );
}

// Placeholder pages for verify-email and reset-password
function VerifyEmail() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-center p-6">
      <div className="space-y-4">
        <div className="h-16 w-16 rounded-2xl bg-green-500/20 flex items-center justify-center mx-auto">
          <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold">Check Your Email</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          We've sent you a verification link. Please check your email and click the link to verify your account.
        </p>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        element: (
          <SuspenseWrapper>
            <Landing />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/login",
        element: (
          <SuspenseWrapper>
            <Login />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/verify-email",
        element: <VerifyEmail />,
      },
      {
        path: "/unlock-account",
        element: (
          <SuspenseWrapper>
            <UnlockAccount />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/privacy",
        element: (
          <SuspenseWrapper>
            <Privacy />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/terms",
        element: (
          <SuspenseWrapper>
            <Terms />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/contact",
        element: (
          <SuspenseWrapper>
            <ContactPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/gifts-for/:slug",
        element: <GiftsForPage />,
      },
      {
        path: "/google-contacts-callback",
        element: (
          <SuspenseWrapper>
            <GoogleContactsCallback />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/cards/view/:shareUrl",
        element: (
          <SuspenseWrapper>
            <CardView />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/gift-dna/:searchId",
        element: (
          <SuspenseWrapper>
            <GiftDna />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/subscription",
        element: (
          <SuspenseWrapper>
            <Subscription />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/pricing",
        element: (
          <SuspenseWrapper>
            <Subscription />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/preferences/:token",
        element: (
          <SuspenseWrapper>
            <PreferenceForm />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/browse",
        element: (
          <SuspenseWrapper>
            <BrowseGifts />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/experiences",
        element: (
          <SuspenseWrapper>
            <Experiences />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/shop",
        element: (
          <SuspenseWrapper>
            <Shop />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/affiliate-disclosure",
        element: (
          <SuspenseWrapper>
            <AffiliateDisclosure />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/unsubscribed",
        element: (
          <SuspenseWrapper>
            <Unsubscribed />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    element: <Layout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Dashboard />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/onboarding",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Onboarding />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/welcome",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Welcome />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/wizard",
        errorElement: <WizardErrorBoundary />,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftWizard />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/results/:searchId",
        errorElement: <WizardErrorBoundary />,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftResults />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/featured",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Featured />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/browse",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <BrowseGifts />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/experiences",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Experiences />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/shop",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Shop />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contacts",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Contacts />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contacts/onboarding",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ContactOnboarding />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contacts/:id",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ContactDetail />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/calendar",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Calendar />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/cards",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Cards />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/cards/create",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <CardEditor />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/cards/:id",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <CardEditor />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/my-cards",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <MyCards />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/cards/:id/analytics",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <CardAnalytics />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/saved-gifts",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <SavedGifts />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/gift-history",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftHistory />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/gift-impact",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftImpact />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/gift-exchange/create",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftExchange />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/gift-exchange/:shareUrl",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GiftExchange />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/group-gift/:shareUrl",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <GroupGift />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/wishlist",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <WishList />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/profile",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <Profile />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/reminders",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ReminderSettings />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/notifications",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <NotificationSettings />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      // Admin routes
      {
        path: "/admin",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminDashboard />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/users",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminUsers />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/users/:id",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminUserDetail />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/content/:tab?",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminContent />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/support",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminSupport />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/email-templates",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminEmailTemplates />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/emails",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminEmailPreview />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/audit-log",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminAuditLog />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/notifications/:tab?",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <NotificationMonitoring />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/deployments",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminDeployments />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/migrations",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminMigrations />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/queues",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminQueueMonitoring />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/share-preview",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminSharePreview />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/og-preview",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminOgPreview />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/analytics",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminAnalytics />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/newsletter",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminNewsletter />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/gsc",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminGSC />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/experience-deals",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminExperienceDeals />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: "/admin/feeds",
        element: (
          <ProtectedRoute requireAdmin>
            <SuspenseWrapper>
              <AdminFeeds />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: "*",
    element: <RouteErrorBoundary />,
  },
]);
